<template>
  <Authenticated>
    <section class="main">
      <div class="unit-container">
        <div v-if="activeDrivers.length > 0" v-for="driver in activeDrivers" :key="driver" class="unit-list">
          <div class="unit" >
            <div class="header">
              <h3>{{ driver.name }}</h3>
              <div class="icon-container">
                <div class="state">
                  <font-awesome-icon icon="truck-fast" :style="{ color: driver.colour }" class="icon"></font-awesome-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="unit"><h3 class="dark">No active Drivers</h3></div>
      </div>
    </section>
    <!-- Map Section -->
    <section class="map-section">
      <div class="header">
        <h2>Drivers</h2>
      </div>
      <div id="map" ref="mapContainer" class="map"></div>
    </section>

    <section class="drivers-section">
      <div class="header">
        <h3>All Drivers</h3>
        <div class="new" @click="openGEOModal({}, true)">Add Driver</div>
      </div>
      <div class="drivers-list">
        <div v-for="driver in getGeoSettings.value" :key="driver.id" class="driver-card" @click="openGEOModal(driver, false)">
          <div class="driver-info">
            <p class="driver-name">{{ driver.name }}</p>
          </div>
        </div>
        <div v-if="!getGeoSettings.value || getGeoSettings.value.length === 0" class="no-drivers">
          <p>No drivers configured</p>
        </div>
      </div>
    </section>

    <div v-if="loading">
      <LoadingSpinner :spinnerMessage="'Updating stations...'" />
    </div>
    <GEOStationModal :open="showGEOModal" :station="GEOStation" :newStation="newStation" @update="updateStations" @close="close" />
  </Authenticated>
</template>

<script>
import Authenticated from "../../_layouts/Authenticated.vue";
import { api } from "@monolith/helpers";
import { mapGetters } from "vuex";
import { CodeLogIn, LoadingSpinner } from "@monolith/components";
import GEOStationModal from "./GEOStationModal.vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

export default {
  name: "GEOPage",
  components: {
    Authenticated,
    CodeLogIn,
    LoadingSpinner,
    GEOStationModal,
  },
  data() {
    return {
      showGEOModal: false,
      GEOStation: {},
      newStation: false,
      openCompanionModal: false,
      map: null,
      markers: [],
      defaultCoords: { lat: -45.871478138862905, lng: 170.50666261494524 },
      activeDrivers: [],
    };
  },
  methods: {
    close() {
      this.showGEOModal = false;
      this.newStation = false;
    },

    openGEOModal(incoming, newStation = false) {
      if (newStation) {
        this.GEOStation = {
          name: null,
          site: "all",
        };
        this.newStation = newStation;
      }
      this.GEOStation = incoming;
      this.showGEOModal = !this.showGEOModal;
    },

    async updateStations(typeOfUpdate, incomingStation) {
      this.$store.dispatch("loading", true);
      let newStations = [];
      this.siteMap.forEach((site) => {
        if (site.site == incomingStation.site) {
          site.stations[typeOfStation].forEach((station) => {
            if (typeOfUpdate === "update") {
              if (station.name == incomingStation.name) {
                newStations.push(this.removeActive(incomingStation));
              } else {
                newStations.push(station);
              }
            } else if (typeOfUpdate === "delete") {
              if (station.name != incomingStation.name) {
                newStations.push(this.removeActive(station));
              }
            }
          });
        } else {
          site.stations[typeOfStation].forEach((station) => {
            newStations.push(station);
          });
        }
      });

      await api.post("/api/company/update_stations", {
        doc: newStations,
        station_type: "get_geo",
        site: incomingStation.site,
      });
      await this.$store.dispatch("initialSiteMap");
      this.$store.dispatch("loading", false);
      this.close();
    },

    removeActive(station) {
      if ("active" in station) {
        delete station.active;
      }
      return station;
    },

    initializeMap() {
      this.map = L.map("map").setView([this.defaultCoords.lat, this.defaultCoords.lng], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(this.map);
    },

    addMarker(lat, lng, iconOptions = {}) {
      if (!this.map) return;

      let marker = L.marker([lat, lng], iconOptions).addTo(this.map);

      this.markers.push(marker);
    },

    getDriverColour(driver) {
      const foundDriver = this.activeDrivers.find((m) => m.name.toLowerCase() === driver.name.toLowerCase());
      return foundDriver ? foundDriver.colour : "#000000";
    },

    generateRandomHexColor() {
      let randomInt = Math.floor(Math.random() * 0xffffff);
      let hexString = randomInt.toString(16);
      let fullHexString = hexString.padStart(6, "0");
      return "#" + fullHexString;
    },

    addDriverMarker(driver, lat, lng) {
      const existingDriver = this.activeDrivers.find((d) => d.name.toLowerCase() === driver.driver_id.toLowerCase());

      let driverColor;
      if (existingDriver) {
        driverColor = existingDriver.colour;
      } else {
        driverColor = this.generateRandomHexColor();
        this.activeDrivers.push({
          colour: driverColor,
          name: driver.driver_id,
        });
      }

      let driverIcon = L.divIcon({
        className: "driver-marker",
        id: `driver-${driver.driver_id}`,
        html: `<div class="driver-dot" style="background-color: ${driverColor};"></div>`,
      });
      
      this.clearMarkers();
      this.addMarker(lat, lng, { icon: driverIcon });
    },

    clearMarkers() {
      this.markers.forEach((marker) => {
        this.map.removeLayer(marker);
      });
      this.markers = [];
    },

    cleanupMap() {
      if (this.map) {
        this.clearMarkers();
        this.map.remove();
        this.map = null;
      }
    },
  },
  computed: {
    ...mapGetters({
      siteMap: "siteMap",
      siteSettings: "siteSettings",
      companySettings: "companySettings",
      timezone: "timezone",
      sites: "sites",
      stripeCustomerDetails: "stripeCustomerDetails",
      getSubscribedProducts: "getSubscribedProducts",
      companyName: "companyName",
      loading: "loading",
      beamyProducts: "beamyProducts",
      getGeoSettings: "getGeoSettings",
      getDriverPositions: "getDriverPositions",
    }),
  },
  mounted() {
    this.$store.dispatch("updateGeoSettings");
    this.$nextTick(() => {
      this.initializeMap();
    });
  },
  beforeUnmount() {
    this.cleanupMap();
  },

  watch: {
    getDriverPositions: {
      handler() {
        this.getDriverPositions.forEach((driver) => {
          if (driver.latitude && driver.longitude) {
            this.addDriverMarker(driver, driver.latitude, driver.longitude);
          }
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";
.main {
  .unit-container {
    position: absolute;
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-start;
    align-items: center;
    border-radius: 1rem;
    margin: 1rem;
    right: 2.25rem;
    top: 5.5rem;
    z-index: 10000;
    gap: 0.5rem;

    .unit {
      width: 16rem;
      padding: 0.25rem;
      box-shadow: var(--box-shadow);
      border-radius: 10px;
      background-color: var(--opacity-background);
      .dark {
        color: var(--secondary-text-colour);
      }
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        backdrop-filter: blur(1px);
        border-radius: 1rem;
        z-index: -1;
      }
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0;
        padding: 0.5rem;
        h3 {
          color: var(--secondary-text-colour);
          padding: 0;
          text-transform: capitalize;
          margin: 0;
        }

        .icon-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          gap: 1rem;

          .state {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            p {
              margin: 0;
            }
            .icon {
              font-size: 1.5rem;
              filter: drop-shadow(var(--box-shadow))
            }
          }
        }
      }
      .btn {
        display: flex;
        justify-content: center;
        margin: 0.5rem;
        min-width: 15rem;
        height: 2.5rem;
        align-items: center;
        background: var(--primary-colour) !important;
        border: none !important;
        color: var(--text-colour);
        padding: 0.5rem;
        cursor: pointer;
      }
      p {
        color: var(--primary-colour);
      }
    }
  }
}

.map-section {
  padding: 1rem;
  box-shadow: var(--box-shadow);
  background-color: var(--primary-colour);
  margin: 2rem;
  border-radius: 10px;
  position: relative;

  .map {
    width: 100%;
    height: 40rem;
    border-radius: 0.5rem;
    box-shadow: var(--box-shadow);
    background-color: var(--tertiary-colour);
    z-index: 10;

    :deep(.driver-marker) {
      .driver-dot {
        border-radius: 50% 50% 0 50%;
        transform: rotate(45deg);
        border: 1px solid var(--primary-colour);
        width: 15px;
        height: 15px;
        box-shadow: var(--box-shadow);
      }
    }
  }
}

// Drivers section styles
.drivers-section {
  padding: 1rem;
  box-shadow: var(--box-shadow);
  background-color: var(--primary-colour);
  margin: 2rem;
  border-radius: 10px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 1rem 0;
    h2 {
      text-transform: capitalize;
      margin-bottom: 1rem;
    }
    .new {
      background-color: var(--secondary-colour);
      color: var(--text-colour);
      border-radius: 0.5rem;
      padding: 1rem 3rem;
      box-shadow: var(--box-shadow);
      cursor: pointer;
      text-align: center;
    }
  }

  .drivers-list {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding: 0.5rem 0;

    .driver-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      background-color: var(--secondary-colour);
      border-radius: 10px;
      padding: 1rem;
      min-width: 200px;
      box-shadow: var(--box-shadow);
      flex-shrink: 0;

      .driver-info {
        margin-bottom: 0.5rem;
        text-align: center;

        .driver-name {
          margin: 0;
          color: var(--text-colour);
          text-transform: capitalize;
        }
      }

      .activate-btn {
        background-color: var(--primary-colour);
        color: var(--text-colour);
        border: none;
        border-radius: 0.5rem;
        padding: 0.5rem 1rem;
        cursor: pointer;
        box-shadow: var(--box-shadow);
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
      }
    }

    .no-drivers {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: 2rem;

      p {
        color: var(--secondary-text-colour);
        margin: 0;
      }
    }
  }
}

@media only screen and (orientation: portrait) {
  .main,
  .map-section,
  .drivers-section {
    padding: 0.5rem 0;
    margin: 1rem !important;
    border-radius: 0.5rem;
    max-width: 95%;

    h2,
    h3 {
      margin-left: 1rem;
    }

    .unit-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .unit {
        width: 16rem;
      }
    }
  }

  .map-section .map {
    height: 300px;
  }

  .drivers-section {
    .drivers-list {
      .driver-card {
        min-width: 150px;
      }
    }
  }
}
</style>
